/* ============================================================
   Tri Nature — Product page redesign behaviour
   - <tn-product>  variant picking, subscription, gallery, ATC
   - <tn-bundle>   bundle builder
   Depends on the theme only for the cart drawer, which listens
   for `ajaxProduct:added` on document (see theme.CartDrawer).
   ============================================================ */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (!format) return '$' + amount;
    // Shopify money_format uses {{amount}} style placeholders.
    return format.replace(/\{\{\s*amount\s*\}\}/, amount)
                 .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
                 .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  /** Tell the theme a line item landed in the cart so the drawer opens. */
  function notifyCart(product, button) {
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
      bubbles: true,
      detail: { product: product, addToCartBtn: button }
    }));
  }

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ==========================================================
     <tn-product>
     ========================================================== */
  class TnProduct extends HTMLElement {
    connectedCallback() {
      this.data = parseJSON(this.querySelector('[data-tnp-variants]')) || {};
      this.variants = this.data.variants || [];
      this.plans = this.data.sellingPlans || [];
      this.moneyFormat = this.data.moneyFormat || '';
      // Loyalty Club percent, 0 for everyone who is not a logged in member.
      this.loyaltyPercent = parseInt(this.data.loyaltyPercent, 10) || 0;
      this.cartType = this.data.cartType || 'drawer';
      this.productHandle = this.data.handle || '';

      this.form = this.querySelector('[data-tnp-form]');
      this.idInput = this.querySelector('[data-tnp-variant-id]');
      this.planInput = this.querySelector('[data-tnp-selling-plan]');
      this.submitBtn = this.querySelector('[data-tnp-submit]');
      this.errorEl = this.querySelector('[data-tnp-error]');

      this.selected = this.currentOptions();
      this.variant = this.findVariant(this.selected) || this.variants.find(function (v) { return v.available; }) || this.variants[0];

      this.bindOptions();
      this.bindPurchase();
      this.bindQty();
      this.bindGallery();
      this.bindAccordions();
      this.bindNotify();
      this.bindSubmit();

      this.render();
    }

    /* ---------- options ---------- */
    currentOptions() {
      var out = [];
      this.querySelectorAll('[data-tnp-option-group]').forEach(function (group) {
        var pos = parseInt(group.dataset.tnpOptionGroup, 10);
        var active = group.querySelector('[aria-pressed="true"]');
        out[pos - 1] = active ? active.dataset.tnpOptionValue : null;
      });
      return out;
    }

    bindOptions() {
      var self = this;
      this.querySelectorAll('[data-tnp-option-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var group = btn.closest('[data-tnp-option-group]');
          group.querySelectorAll('[data-tnp-option-value]').forEach(function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
          self.selected = self.currentOptions();
          var match = self.findVariant(self.selected);
          if (match) {
            self.variant = match;
            self.notifyEmailSent = false;
          }
          self.render();
          self.syncUrl();
        });
      });
    }

    findVariant(options) {
      return this.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === options[i]; });
      });
    }

    /** Mark option values that lead to no purchasable variant. */
    markAvailability() {
      var self = this;
      this.querySelectorAll('[data-tnp-option-group]').forEach(function (group) {
        var pos = parseInt(group.dataset.tnpOptionGroup, 10);
        group.querySelectorAll('[data-tnp-option-value]').forEach(function (btn) {
          var probe = self.selected.slice();
          probe[pos - 1] = btn.dataset.tnpOptionValue;
          var v = self.findVariant(probe);
          btn.dataset.unavailable = String(!v || !v.available);
        });
      });
    }

    syncUrl() {
      if (!this.variant || !window.history || !history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', this.variant.id);
      history.replaceState({}, '', url.toString());
    }

    /* ---------- purchase type ---------- */
    bindPurchase() {
      var self = this;
      this.purchaseMode = this.plans.length ? (this.data.defaultPurchase || 'onetime') : 'onetime';
      this.planId = this.plans.length ? this.plans[0].id : '';

      this.querySelectorAll('[data-tnp-purchase]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.purchaseMode = btn.dataset.tnpPurchase;
          self.querySelectorAll('[data-tnp-purchase]').forEach(function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
          self.render();
        });
      });

      this.querySelectorAll('[data-tnp-plan]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.planId = btn.dataset.tnpPlan;
          self.querySelectorAll('[data-tnp-plan]').forEach(function (b) {
            b.setAttribute('aria-pressed', String(b === btn));
          });
          self.render();
        });
      });
    }

    /**
     * Base price the loyalty percent comes off. Mirrors
     * snippets/product-grid-item.liquid: when a compare at price exists it is
     * the base, otherwise the selling price is.
     */
    cgBase(v) {
      if (!v) return 0;
      var compare = v.compareAtPrice || 0;
      return compare > v.price ? compare : v.price;
    }

    /**
     * One-time price for a variant with the Loyalty Club percent applied.
     * The integer maths matches Liquid's `times, plus 99, divided_by 100`,
     * which is a ceiling, so the page and the collection card round the same
     * way to the cent.
     */
    loyaltyPrice(v) {
      if (!v) return 0;
      if (!this.loyaltyPercent) return v.price;
      return Math.ceil(this.cgBase(v) * (100 - this.loyaltyPercent) / 100);
    }

    /** Price for the current variant under the current purchase mode. */
    priceNow() {
      if (!this.variant) return 0;
      if (this.purchaseMode !== 'subscribe') return this.loyaltyPrice(this.variant);
      var alloc = this.planAllocation();
      return alloc ? alloc.price : this.loyaltyPrice(this.variant);
    }

    planAllocation() {
      if (!this.variant || !this.variant.sellingPlanAllocations) return null;
      var id = String(this.planId);
      return this.variant.sellingPlanAllocations.find(function (a) {
        return String(a.sellingPlanId) === id;
      }) || null;
    }

    /* ---------- quantity ---------- */
    bindQty() {
      var self = this;
      this.qtyInput = this.querySelector('[data-tnp-qty]');
      var dec = this.querySelector('[data-tnp-qty-dec]');
      var inc = this.querySelector('[data-tnp-qty-inc]');
      if (dec) dec.addEventListener('click', function () { self.setQty(self.qty() - 1); });
      if (inc) inc.addEventListener('click', function () { self.setQty(self.qty() + 1); });
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', function () { self.setQty(self.qty()); });
      }
    }

    qty() {
      var n = parseInt(this.qtyInput && this.qtyInput.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    }

    setQty(n) {
      var v = Math.max(1, n || 1);
      if (this.qtyInput) this.qtyInput.value = v;
      var dec = this.querySelector('[data-tnp-qty-dec]');
      if (dec) dec.disabled = v <= 1;
    }

    /* ---------- gallery ---------- */
    bindGallery() {
      var self = this;
      this.mainImg = this.querySelector('[data-tnp-main-image]');
      this.images = Array.prototype.map.call(
        this.querySelectorAll('[data-tnp-thumb]'),
        function (t) { return { full: t.dataset.tnpFull, alt: t.dataset.tnpAlt || '', id: t.dataset.tnpMediaId }; }
      );
      this.imageIndex = 0;

      this.querySelectorAll('[data-tnp-thumb]').forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { self.showImage(i); });
      });

      this.lightbox = this.querySelector('[data-tnp-lightbox]');
      var open = this.querySelector('[data-tnp-lightbox-open]');
      if (open) open.addEventListener('click', function () { self.openLightbox(); });
      this.querySelectorAll('[data-tnp-lightbox-close]').forEach(function (b) {
        b.addEventListener('click', function () { self.closeLightbox(); });
      });
      var prev = this.querySelector('[data-tnp-lightbox-prev]');
      var next = this.querySelector('[data-tnp-lightbox-next]');
      if (prev) prev.addEventListener('click', function () { self.step(-1); });
      if (next) next.addEventListener('click', function () { self.step(1); });

      this._onKey = function (e) {
        if (!self.lightbox || self.lightbox.hidden) return;
        if (e.key === 'Escape') self.closeLightbox();
        if (e.key === 'ArrowLeft') self.step(-1);
        if (e.key === 'ArrowRight') self.step(1);
      };
      document.addEventListener('keydown', this._onKey);
    }

    disconnectedCallback() {
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
    }

    showImage(i) {
      if (!this.images.length) return;
      this.imageIndex = (i + this.images.length) % this.images.length;
      var img = this.images[this.imageIndex];
      if (this.mainImg) {
        this.mainImg.src = img.full;
        this.mainImg.alt = img.alt;
      }
      var lbImg = this.querySelector('[data-tnp-lightbox-image]');
      if (lbImg) { lbImg.src = img.full; lbImg.alt = img.alt; }
      this.querySelectorAll('[data-tnp-thumb]').forEach(function (t, n) {
        t.setAttribute('aria-current', String(n === i));
      });
    }

    step(d) { this.showImage(this.imageIndex + d); }

    openLightbox() {
      if (!this.lightbox) return;
      this.lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      var close = this.lightbox.querySelector('[data-tnp-lightbox-close]');
      if (close) close.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.hidden = true;
      document.body.style.overflow = '';
      var open = this.querySelector('[data-tnp-lightbox-open]');
      if (open) open.focus();
    }

    /* ---------- accordions ---------- */
    bindAccordions() {
      this.querySelectorAll('[data-tnp-acc-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var panel = document.getElementById(trigger.getAttribute('aria-controls'));
          if (!panel) return;
          var open = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', String(!open));
          panel.hidden = open;
          var sign = trigger.querySelector('[data-tnp-acc-sign]');
          if (sign) sign.textContent = open ? '+' : '−';
        });
      });
    }

    /* ---------- back in stock ---------- */
    bindNotify() {
      var self = this;
      var btn = this.querySelector('[data-tnp-notify-submit]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var input = self.querySelector('[data-tnp-notify-email]');
        var done = self.querySelector('[data-tnp-notify-done]');
        var pending = self.querySelector('[data-tnp-notify-pending]');
        var email = (input && input.value || '').trim();
        if (!email || email.indexOf('@') === -1) {
          if (input) input.focus();
          return;
        }
        btn.disabled = true;
        self.submitNotify(email).then(function () {
          if (pending) pending.hidden = true;
          if (done) done.hidden = false;
        }).catch(function () {
          btn.disabled = false;
          if (self.errorEl) {
            self.errorEl.textContent = 'We could not save that just now. Please try again.';
            self.errorEl.hidden = false;
          }
        });
      });
    }

    /**
     * Back-in-stock signup via Klaviyo's client subscriptions endpoint.
     * Consent is implied by the endpoint; do not send a subscriptions block.
     */
    submitNotify(email) {
      var cfg = this.data.klaviyo || {};
      if (!cfg.companyId || !cfg.listId) return Promise.resolve();
      return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    properties: {
                      'Back In Stock Product': this.data.title || '',
                      'Back In Stock Variant': this.variant ? this.variant.title : '',
                      'Back In Stock Variant ID': this.variant ? String(this.variant.id) : '',
                      'Back In Stock URL': window.location.href
                    }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: cfg.listId } } }
          }
        })
      }).then(function (r) {
        if (!r.ok && r.status !== 202) throw new Error('klaviyo');
      });
    }

    /* ---------- submit ---------- */
    bindSubmit() {
      var self = this;
      if (!this.form) return;
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.addToCart();
      });
    }

    addToCart() {
      var self = this;
      if (!this.variant || !this.variant.available) return;

      if (this.errorEl) this.errorEl.hidden = true;
      this.submitBtn.classList.add('is-loading');
      this.submitBtn.disabled = true;

      var body = {
        items: [{
          id: this.variant.id,
          quantity: this.qty()
        }]
      };
      if (this.purchaseMode === 'subscribe' && this.planId) {
        body.items[0].selling_plan = Number(this.planId);
      }

      fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw res.data;
          if (self.cartType === 'page') {
            window.location = ROOT + 'cart';
            return;
          }
          notifyCart(res.data, self.submitBtn);
        })
        .catch(function (err) {
          if (self.errorEl) {
            self.errorEl.textContent = (err && (err.description || err.message)) || 'Sorry, we could not add that to your cart.';
            self.errorEl.hidden = false;
          }
        })
        .finally(function () {
          self.submitBtn.classList.remove('is-loading');
          self.submitBtn.disabled = false;
          self.render();
        });
    }

    /* ---------- render ---------- */
    render() {
      var v = this.variant;
      this.markAvailability();

      if (this.idInput && v) this.idInput.value = v.id;

      var subscribing = this.purchaseMode === 'subscribe' && this.plans.length > 0;
      if (this.planInput) this.planInput.value = subscribing ? this.planId : '';

      var freq = this.querySelector('[data-tnp-freq]');
      if (freq) freq.hidden = !subscribing;

      // Price
      var now = this.priceNow();
      var priceEl = this.querySelector('[data-tnp-price]');
      if (priceEl) priceEl.textContent = money(now, this.moneyFormat);

      var wasEl = this.querySelector('[data-tnp-price-compare]');
      if (wasEl) {
        var compare;
        if (subscribing) compare = v && v.price;
        else if (this.loyaltyPercent) compare = this.cgBase(v);
        else compare = v && v.compareAtPrice;
        var showCompare = compare && compare > now;
        wasEl.hidden = !showCompare;
        if (showCompare) wasEl.textContent = money(compare, this.moneyFormat);
      }

      // Loyalty Club note under the price
      var loyaltyNote = this.querySelector('[data-tnp-loyalty-note]');
      if (loyaltyNote) {
        var showLoyalty = !!this.loyaltyPercent && !subscribing && !!v;
        loyaltyNote.hidden = !showLoyalty;
        if (showLoyalty) {
          loyaltyNote.textContent = (this.data.loyaltyLabel || 'Loyalty price')
            + ' \u00b7 save ' + money(this.cgBase(v) - now, this.moneyFormat);
        }
      }

      // Per-option prices on the size buttons
      var self = this;
      this.querySelectorAll('[data-tnp-option-price]').forEach(function (el) {
        var group = el.closest('[data-tnp-option-group]');
        var btn = el.closest('[data-tnp-option-value]');
        if (!group || !btn) return;
        var probe = self.selected.slice();
        probe[parseInt(group.dataset.tnpOptionGroup, 10) - 1] = btn.dataset.tnpOptionValue;
        var pv = self.findVariant(probe);
        if (pv) el.textContent = money(self.loyaltyPrice(pv), self.moneyFormat);
      });

      // Subscription switch prices
      var onceEl = this.querySelector('[data-tnp-once-price]');
      if (onceEl && v) onceEl.textContent = money(this.loyaltyPrice(v), this.moneyFormat);
      var subEl = this.querySelector('[data-tnp-sub-price]');
      var alloc = this.planAllocation();
      if (subEl && v) subEl.textContent = money(alloc ? alloc.price : v.price, this.moneyFormat);
      // The subscription price is Appstle's and is left exactly as published.
      // Both the strike and the saving are measured against the one-time price
      // this shopper would actually pay, so a loyalty member is never shown a
      // saving they will not get.
      var onceForCompare = this.loyaltyPrice(v);
      // Under five cents the line reads as noise, which is exactly what a
      // loyalty member sees: Appstle's subscription discount is the same 5%
      // the member already gets, so subscribing saves them a cent. Show the
      // plain reassurance instead of a silly number.
      var subSaving = alloc ? onceForCompare - alloc.price : 0;
      var hasSaving = subSaving >= 5;
      var subStrike = this.querySelector('[data-tnp-sub-strike]');
      if (subStrike && v) {
        subStrike.hidden = !hasSaving;
        if (hasSaving) subStrike.textContent = money(onceForCompare, this.moneyFormat);
      }
      var savingNote = this.querySelector('[data-tnp-saving]');
      if (savingNote && v) {
        savingNote.textContent = hasSaving
          ? 'Save ' + money(subSaving, this.moneyFormat) + ' each delivery · skip or cancel any time'
          : 'Skip or cancel any time';
      }

      // Stock
      var stock = this.querySelector('[data-tnp-stock]');
      if (stock && v) {
        var available = v.available;
        stock.dataset.state = available ? 'in' : 'out';
        var label = stock.querySelector('[data-tnp-stock-label]');
        if (label) {
          label.textContent = available
            ? (this.data.inStockLabel || 'In stock, ready to ship')
            : (this.data.outOfStockLabel || 'Currently unavailable');
        }
      }

      // Back in stock panel
      var notify = this.querySelector('[data-tnp-notify]');
      if (notify) notify.hidden = !(v && !v.available);

      // Size label in headings
      this.querySelectorAll('[data-tnp-variant-title]').forEach(function (el) {
        if (v) el.textContent = v.title;
      });

      // CTA
      if (this.submitBtn && v) {
        var labelEl = this.submitBtn.querySelector('[data-tnp-cta-label]');
        var text;
        if (!v.available) text = this.data.soldOutLabel || 'Sold out';
        else if (subscribing) text = (this.data.subscribeLabel || 'Subscribe') + ' · ' + money(now, this.moneyFormat);
        else text = (this.data.addLabel || 'Add to cart') + ' · ' + money(now, this.moneyFormat);
        if (labelEl) labelEl.textContent = text; else this.submitBtn.textContent = text;
        this.submitBtn.disabled = !v.available;
      }

      // Swap gallery to the variant's own image
      if (v && v.featuredMediaId) {
        var idx = this.images.findIndex(function (im) { return String(im.id) === String(v.featuredMediaId); });
        if (idx > -1 && idx !== this.imageIndex) this.showImage(idx);
      }

      // Let colocated blocks (express checkout, instalments, pickup) follow the
      // selection without reaching into this class.
      this.dispatchEvent(new CustomEvent('tnp:variant', {
        bubbles: true,
        detail: {
          variant: v,
          price: now,
          available: !!(v && v.available),
          subscribing: subscribing
        }
      }));
    }
  }

  /* ==========================================================
     <tn-bundle>
     ========================================================== */
  class TnBundle extends HTMLElement {
    connectedCallback() {
      this.data = parseJSON(this.querySelector('[data-tnp-bundle-data]')) || {};
      this.items = this.data.items || [];
      this.tiers = this.data.tiers || [];
      this.moneyFormat = this.data.moneyFormat || '';
      this.cartType = this.data.cartType || 'drawer';

      this.selection = this.items.map(function (it) { return it.available !== false; });

      var self = this;
      this.querySelectorAll('[data-tnp-bundle-item]').forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          self.selection[i] = !self.selection[i];
          btn.setAttribute('aria-pressed', String(self.selection[i]));
          self.render();
        });
      });

      this.addBtn = this.querySelector('[data-tnp-bundle-add]');
      this.errorEl = this.querySelector('[data-tnp-bundle-error]');
      if (this.addBtn) this.addBtn.addEventListener('click', function () { self.addBundle(); });

      this.render();
    }

    chosen() {
      var self = this;
      return this.items.filter(function (it, i) { return self.selection[i]; });
    }

    /** Highest tier whose threshold the selection meets. */
    tierFor(count) {
      var match = null;
      this.tiers.forEach(function (t) {
        if (count >= t.quantity && (!match || t.quantity > match.quantity)) match = t;
      });
      return match;
    }

    render() {
      var chosen = this.chosen();
      var full = chosen.reduce(function (t, it) { return t + it.price; }, 0);
      var tier = this.tierFor(chosen.length);
      var saving = tier ? Math.round(full * (tier.percentage / 100)) : 0;

      var set = function (sel, val) {
        var el = this.querySelector(sel);
        if (el) el.textContent = val;
      }.bind(this);

      set('[data-tnp-bundle-count]', chosen.length === 1 ? '1 product' : chosen.length + ' products');
      set('[data-tnp-bundle-full]', money(full, this.moneyFormat));
      set('[data-tnp-bundle-total]', money(full - saving, this.moneyFormat));
      set('[data-tnp-bundle-saving]', '− ' + money(saving, this.moneyFormat));

      var savingRow = this.querySelector('[data-tnp-bundle-saving-row]');
      if (savingRow) savingRow.hidden = saving === 0;

      var tierLabel = this.querySelector('[data-tnp-bundle-tier]');
      if (tierLabel) {
        if (tier) {
          tierLabel.textContent = 'Saving ' + tier.percentage + '% on this set';
        } else {
          var next = this.tiers.slice().sort(function (a, b) { return a.quantity - b.quantity; })[0];
          tierLabel.textContent = next
            ? 'Add ' + Math.max(1, next.quantity - chosen.length) + ' more to save ' + next.percentage + '%'
            : '';
        }
      }

      if (this.addBtn) this.addBtn.disabled = chosen.length === 0;
    }

    addBundle() {
      var self = this;
      var chosen = this.chosen();
      if (!chosen.length) return;

      if (this.errorEl) this.errorEl.hidden = true;
      this.addBtn.classList.add('is-loading');
      this.addBtn.disabled = true;

      var tier = this.tierFor(chosen.length);
      var pre = (tier && tier.discountCode)
        ? fetch(ROOT + 'discount/' + encodeURIComponent(tier.discountCode), { method: 'GET' }).catch(function () {})
        : Promise.resolve();

      pre.then(function () {
        return fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: chosen.map(function (it) { return { id: it.variantId, quantity: 1 }; })
          })
        });
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) throw res.data;
          if (self.cartType === 'page') { window.location = ROOT + 'cart'; return; }
          notifyCart(res.data, self.addBtn);
        })
        .catch(function (err) {
          if (self.errorEl) {
            self.errorEl.textContent = (err && (err.description || err.message)) || 'Sorry, we could not add that set to your cart.';
            self.errorEl.hidden = false;
          }
        })
        .finally(function () {
          self.addBtn.classList.remove('is-loading');
          self.render();
        });
    }
  }

  if (!customElements.get('tn-product')) customElements.define('tn-product', TnProduct);
  if (!customElements.get('tn-bundle')) customElements.define('tn-bundle', TnBundle);
})();
